import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  HotelRoomRateMeta,
  HotelRoomRateSelection,
} from "@b2bportal/core-types";
import type { RoomInfoProducts } from "@b2bportal/lodging-api";
import { useHotelRoomRateSources } from "./HotelRoomRateSourcesProvider";

export interface UseHotelRoomRatesProps {
  room: RoomInfoProducts;
}

export const useHotelRoomRates = ({
  room,
}: UseHotelRoomRatesProps): HotelRoomRateSelection => {
  const { hooks, transform, setDeselector, deselect } =
    useHotelRoomRateSources();
  const [selectedRate, setSelectedRateState] =
    useState<HotelRoomRateMeta | null>(null);

  const setSelectedRate = useCallback(
    (rate: HotelRoomRateMeta) => {
      deselect();

      setSelectedRateState(rate);

      const deselector = rate.setSelectedRate();
      if (typeof deselector === "function") {
        setDeselector(deselector);
      }
    },
    [deselect, setDeselector]
  );

  const sourceResults = hooks.map((hook) => hook({ room }));

  // Flatten and combine all rates
  const rates = useMemo(() => {
    const allRates = sourceResults.map((result) => result.rates ?? []).flat();
    return transform(allRates);
  }, [sourceResults, transform]);

  // Auto-select first rate
  useEffect(() => {
    if (selectedRate == null && rates.length > 0) {
      setSelectedRate(rates[0]);
    }
  }, [rates, selectedRate, setSelectedRate]);

  const effectiveSelectedRate = selectedRate ?? rates[0];

  return {
    rates,
    selectedRate: effectiveSelectedRate,
    setSelectedRate,
  };
};
